import * as zod from 'zod';

import { CartesianPose } from '@sb/geometry';
import { ErrorCode } from '@sb/messenger/auto_generated/messenger/vision_bot_service';

// DinoFastPrecision and DinoFastReferenceEmbedding are used locally by
// DinoFastParams below.
import {
  CustomCheckpointModelType,
  Detection3DPoseEstimationConfig,
  DinoFastPrecision,
  DinoFastReferenceEmbedding,
} from './visionConfigSchemas';

// Re-exported from the geometry-free `visionConfigSchemas` leaf so the
// skill-config layer (`@sb/ai`) can consume these primitives without dragging
// in `@sb/geometry`/`three`. See visionConfigSchemas.ts for the rationale.
export {
  carryOverDinoFastPrecision,
  CustomCheckpointModelType,
  defaultDinoFastPrecision,
  Detection3DPoseEstimationConfig,
  DinoFastPrecision,
  DinoFastReferenceEmbedding,
  ProjectionMethodEnum,
  projectionMethodNeedsDepth,
  ResultOrdering,
} from './visionConfigSchemas';
export type { ProjectionMethod } from './visionConfigSchemas';

// blob 2D detection method

export const Blob2D = zod.object({
  score: zod.number(),
  contour: zod
    .array(
      zod.object({
        x: zod.number(),
        y: zod.number(),
      }),
    )
    .default([]),
  meanIntensity: zod.number().optional(),
  circularity: zod.number().optional(),
  area: zod.number().optional(),
  classId: zod.number().optional(),
  className: zod.string().optional(),
  maskRle: zod.instanceof(Uint8Array).optional(),
  maskWidth: zod.number().optional(),
  maskHeight: zod.number().optional(),
});

export type Blob2D = zod.infer<typeof Blob2D>;

export const Detection2D = zod.object({
  x: zod.number(),
  y: zod.number(),
  width: zod.number(),
  height: zod.number(),
  // Radians, absent when the detector produced an AXIS-ALIGNED box and never
  // measured an orientation. Optional rather than defaulted to 0 because these
  // detections are sent BACK to vision-bot for pose estimation, where the
  // difference decides whether the box's long side may be used as a grasp axis
  // — a 0 stamped here would read as a measurement. Render it as 0; do not
  // infer an orientation from it.
  rotation: zod.number().optional(),
  score: zod.number(),
  classId: zod.number().optional(),
  className: zod.string().optional(),
});

export type Detection2D = zod.infer<typeof Detection2D>;

export const LocateMatch = zod.object({
  detection: Detection2D,
  segment: Blob2D.nullable().default(null),
});

export type LocateMatch = zod.infer<typeof LocateMatch>;

/**
 * The class name a match reports, preferring the segment's where present.
 *
 * Beside the type because three layers have to agree on it: the camera overlay
 * colours a box by it, the Inspect wizard's panel counts by it, and the runner
 * derives a routine variable per class from it. Written out separately in each,
 * it is three chances for the boxes on the frame, the count beside them and the
 * count a routine branches on to disagree about which defect a detection is —
 * and every one of those disagreements reads as a working skill.
 *
 * The segment wins where both carry a name: segmentation refines a box the
 * detector already classified, so the segment's name is the later word.
 */
export function blobClassName(match: LocateMatch): string | undefined {
  return match.segment?.className ?? match.detection.className;
}

export const Extents3D = zod.object({
  x: zod.number(),
  y: zod.number(),
  z: zod.number(),
});

export type Extents3D = zod.infer<typeof Extents3D>;

// Where a match's in-plane grasp orientation came from, mirroring vision-bot's
// per-detection OrientationSource: the taught template, the detection's own
// principal axis, or the wrist angle every "cannot tell" path lands on.
// `template` covers a flip-resolved registration and one whose flip evidence was
// too thin to publish (reported mod 180) — one registration, two precisions.
// Absent when the grasp frame was not `object` — the plane and world frames
// are definite directions, not measurements, so there is nothing to
// attribute. The UI crosses this with whether a template is saved to tell
// "the template missed on this match" (`pcaAxes` with a template) from
// "there was never a template" (`pcaAxes` without one).
export const OrientationSource = zod.enum(['template', 'pcaAxes', 'wrist']);

export type OrientationSource = zod.infer<typeof OrientationSource>;

// The locate result carries the detector BOX (Detection2D), the resolved 6D
// pose, and — when segmentation ran — the SAM2 `segment` (contour) echoed back
// from vision-bot's plane-projection response. Consumers read
// `.blob.{x,y,width,height,rotation}` for the box and `.segment.contour` for the
// outline (null when segmentation was off or produced no contour).
export const LocateResult = zod.object({
  blob: Detection2D,
  pose: CartesianPose,
  // Where this match sat before the skill's offset moved it — the projected
  // detection itself. `pose` is where the arm goes; this is what it was
  // measured from, and the difference between the two IS the offset, which is
  // what lets a preview draw the offset without re-deriving it from the
  // configured numbers. Absent when the pose was never offset.
  poseBeforeOffset: CartesianPose.optional(),
  extents: Extents3D.optional(),
  segment: Blob2D.nullable().default(null),
  // Geometric plane detection only: the surface fit for this match was rejected
  // or failed, so the pose is a top-down guess (world axes) rather than a
  // measured surface normal. Surfaced so a guessed grasp never reads as a
  // measured one.
  usedTopDownFallback: zod.boolean().optional(),
  // Mirrors vision-bot's per-detection OrientationSource; see above. Absent
  // when the grasp frame was not `object`, or from a vision-bot that predates
  // the field.
  orientationSource: OrientationSource.optional(),
});

export type LocateResult = zod.infer<typeof LocateResult>;

export const Blob2DParams = zod.object({
  minArea: zod.number().default(1000),
  maxArea: zod.number().default(10000),
  minThreshold: zod.number().default(50),
  maxThreshold: zod.number().default(220),
  minCircularity: zod.number().default(0.1),
  maxCircularity: zod.number().default(1),
  filterByCircularity: zod.boolean().default(false),
  invertImage: zod.boolean().default(false),
  disableRotation: zod.boolean().default(false),
});

export type Blob2DParams = zod.infer<typeof Blob2DParams>;

export const BlobDetection2DMethod = zod.object({
  kind: zod.literal('BlobDetection2D'),
  settings: Blob2DParams,
});

export type BlobDetection2DMethod = zod.infer<typeof BlobDetection2DMethod>;

// Shape 2D detection method

export const Shape2DParams = zod.object({
  minScale: zod.number().default(0.8),
  maxScale: zod.number().default(1.2),
  maxRotation: zod.number().default(180),
  matchThreshold: zod.number().default(0.5),
  boundingBox: zod
    .object({
      rotationDegrees: zod.number().default(0),
      scaleX: zod.number().default(1),
      scaleY: zod.number().default(1),
    })
    .default({}),
  filterIntersections: zod.boolean().default(true),
  disableRotation: zod.boolean().default(false),
  rejectOnEdge: zod.boolean().default(false),
});

export type Shape2DParams = zod.infer<typeof Shape2DParams>;

export const TemplateImage = zod.object({
  storageID: zod.string(),
});

export type TemplateImage = zod.infer<typeof TemplateImage>;

export const ShapeDetection2DMethod = zod.object({
  kind: zod.literal('ShapeDetection2D'),
  templateImage: TemplateImage,
  settings: Shape2DParams,
});

export type ShapeDetection2DMethod = zod.infer<typeof ShapeDetection2DMethod>;

// Chessboard detection method
export const ChessboardDetection2DMethod = zod.object({
  kind: zod.literal('ChessboardDetection2D'),
});

export type ChessboardDetection2DMethod = zod.infer<
  typeof ChessboardDetection2DMethod
>;

// Text prompt detection method
export const TextPromptDetection2DParams = zod.object({
  gpuTaskRunnerId: zod.string().default(''),
  textInput: zod.string().default(''),
  useSam: zod.boolean().default(false),
  confidenceThreshold: zod.number().default(0.35),
  // Match-area bounds in cm², measured on the surface each match is projected
  // onto (the contour when segmented, else the detector box). Only ManualPlane
  // and the depth projections can measure it; anywhere else every match is
  // kept. See `matchAreaMetric`.
  minMatchAreaCm2: zod.number().min(0).optional(),
  // A value at/above LOCATE_MAX_MATCH_AREA_CM2 means "no upper limit".
  maxMatchAreaCm2: zod.number().min(0).optional(),
});

export type TextPromptDetection2DParams = zod.infer<
  typeof TextPromptDetection2DParams
>;

export const TextPromptDetection2DMethod = zod.object({
  kind: zod.literal('TextPromptDetection2D'),
  settings: TextPromptDetection2DParams,
});

export type TextPromptDetection2DMethod = zod.infer<
  typeof TextPromptDetection2DMethod
>;

export const CustomCheckpointDetection2DParams = zod.object({
  gpuTaskRunnerId: zod.string(),
  // Checkpoint fields stay required (the skill config defaults them, so a
  // Roboflow-source method still carries modelPath='' etc.) — keeping them
  // required avoids widening the type for existing settings consumers.
  modelPath: zod.string(),
  modelType: CustomCheckpointModelType,
  thresholdingParams: zod.record(zod.string(), zod.string()),
  // Model source: 'checkpoint' (a runner-hosted checkpoint file) or 'roboflow'
  // (a Roboflow-hosted model run via the GPU task runner). Optional (not
  // defaulted) so it isn't forced into callers that build settings literals;
  // absent is treated as 'checkpoint' at runtime.
  modelSource: zod.enum(['checkpoint', 'roboflow']).optional(),
  // Roboflow-source fields (present when modelSource === 'roboflow'). A Roboflow
  // source is always a workflow: the model path this replaced is retired, and a
  // skill saved against it fails validation rather than being migrated.
  //
  // A workflow is a graph that can publish several outputs, so a selection is
  // only complete with the output to read — hence the output name alongside the
  // id — and the workspace, because the run resolves the workflow against the
  // workspace whose stored key it is made with.
  roboflowWorkspaceName: zod.string().optional(),
  roboflowWorkflowId: zod.string().optional(),
  roboflowOutputName: zod.string().optional(),
  // Values bound to the workflow's own inputs at run time, to stop it filtering
  // internally (confidence 0 / iou 1) so this skill's thresholdingParams are the
  // only thing that filters. Resolved when the workflow is chosen, not here:
  // passing an input a workflow does not declare is an error, so only its schema
  // can say which of these are safe to send. Absent means the workflow filters at
  // its own defaults and this skill can only narrow further.
  roboflowWorkflowInputs: zod.record(zod.string(), zod.string()).optional(),
});

export type CustomCheckpointDetection2DParams = zod.infer<
  typeof CustomCheckpointDetection2DParams
>;

export const CustomCheckpointDetection2DMethod = zod.object({
  kind: zod.literal('CustomCheckpointDetection2D'),
  settings: CustomCheckpointDetection2DParams,
});

export type CustomCheckpointDetection2DMethod = zod.infer<
  typeof CustomCheckpointDetection2DMethod
>;

// DinoFast detection method
// (DinoFastPrecision and DinoFastReferenceEmbedding are defined in
// visionConfigSchemas and re-exported above.)

// Serialized weights for the learned locate scorer.
// Trained server-side by vision-bot. When present this replaces the cosine
// scorer; when null, locate falls back to cosine. `kind` selects the model:
// 'mlp' (single-hidden-layer net) or 'logistic' (linear regression).
const DinoFastMlpHeadWeights = zod.object({
  kind: zod.literal('mlp'),
  version: zod.literal(1),
  inputDim: zod.number().int(),
  hidden: zod.number().int(),
  w1: zod.array(zod.number()), // hidden * inputDim, row-major [h][i]
  b1: zod.array(zod.number()), // hidden
  w2: zod.array(zod.number()), // hidden
  b2: zod.number(),
});

const DinoFastLogisticHeadWeights = zod.object({
  kind: zod.literal('logistic'),
  version: zod.literal(1),
  inputDim: zod.number().int(),
  w: zod.array(zod.number()), // inputDim
  b: zod.number(),
});

export const DinoFastLocateHead = zod.discriminatedUnion('kind', [
  DinoFastMlpHeadWeights,
  DinoFastLogisticHeadWeights,
]);

export type DinoFastLocateHead = zod.infer<typeof DinoFastLocateHead>;

// embeddingModelVersion is a free-form string (not a zod.literal) so saved
// configs survive a deployment-time encoder swap and the wizard can surface
// the mismatch as a recoverable error rather than failing to deserialize.
//
// scoreThreshold gates the heatmap and is interpreted against whichever scorer
// produced it: cosine similarity (no locateHead) or the learned head's sigmoid
// output (locateHead set). The schema bound is [-2, 2] to admit any legacy cosine config
// — the cosine effective range with the hard-coded 0.8 negative weight is
// [-1.8, 1.8], and sigmoid scores are [0, 1] — but the teach-UI slider has
// always constrained user-set thresholds to [0, 1], which is sensible for both
// scorers, and the teach modal previews blobs live against the trained head so
// the threshold is calibrated against the actual scoring function.
export const DinoFastParams = zod.object({
  gpuTaskRunnerId: zod.string().default(''),
  embeddingModelVersion: zod.string().default('dinov3-vits16-v1'),
  embeddingDim: zod.number().int().default(384),
  // Defaults to the local-vision (CPU) tier because `gpuTaskRunnerId` defaults
  // to '' — see `defaultDinoFastPrecision`, which the wizards call when the
  // runner changes.
  precision: DinoFastPrecision.default('standard'),
  positives: zod.array(DinoFastReferenceEmbedding).default([]),
  negatives: zod.array(DinoFastReferenceEmbedding).default([]),
  // Learned scorer trained in the teach UI. Null on legacy/untaught steps,
  // which fall back to cosine scoring.
  locateHead: DinoFastLocateHead.nullable().default(null),
  scoreThreshold: zod.number().min(-2).max(2).default(0.5),
  // Match-area bounds in cm²; see TextPromptDetection2DParams.
  minMatchAreaCm2: zod.number().min(0).optional(),
  maxMatchAreaCm2: zod.number().min(0).optional(),
  nmsIouThreshold: zod.number().min(0).max(1).default(0.3),
  maxBlobs: zod.number().int().min(1).default(20),
  // When true (the default), each detected bbox is refined by the backend named
  // in `segmentationMethod` into a contour-only segment (pixel-accurate mask)
  // attached to the match; the detector box stays the raw DinoFast OBB. When
  // false, segmentation is skipped and the match carries no segment. Decoupled
  // from `precision` (which only controls the DINO encoder grid resolution), so
  // any precision can run with or without segmentation. Defaults to true to
  // preserve the prior standard/fine behavior, which always segmented.
  enableSegmentationMask: zod.boolean().default(true),
  // Which segmentation backend refines DinoFast OBBs into masks when
  // `enableSegmentationMask` is on. 'sam2' is the prompt-based segmenter;
  // 'bilateral_solver' is a CPU-only edge-aware refiner of the DINO heatmap.
  // Only consulted when enableSegmentationMask is true.
  //
  // Defaults to 'bilateral_solver' on both devices: it snaps the heatmap to
  // image edges for a contour close to SAM2's at a fraction of the cost, and it
  // runs once over the frame rather than once per detection, so a scene with
  // many matches stays interactive. 'sam2' recovers the truest silhouette — it
  // is the only backend that segments the image rather than the heatmap — but
  // vision-bot runs it per detection at ~0.5s per match on a control-box CPU
  // (sam2.1-hiera-tiny at 512px), so it is opt-in.
  //
  // Legacy skills may have persisted the removed 'guided_filter' backend, so
  // coerce it to 'bilateral_solver' (the CPU refiner it was redundant with, now
  // the default) rather than throwing on parse. New rows never write it — it is
  // gone from the UI and the enum below.
  segmentationMethod: zod
    .preprocess(
      (v) => (v === 'guided_filter' ? 'bilateral_solver' : v),
      zod.enum(['sam2', 'bilateral_solver', 'heatmap_threshold']),
    )
    .default('bilateral_solver'),
});

export type DinoFastParams = zod.infer<typeof DinoFastParams>;

export const DinoFastMethod = zod.object({
  kind: zod.literal('DinoFast'),
  settings: DinoFastParams,
});

export type DinoFastMethod = zod.infer<typeof DinoFastMethod>;

export const ManualBoundingBoxParams = zod.object({
  cx: zod.number().default(640),
  cy: zod.number().default(360),
  width: zod.number().default(100),
  height: zod.number().default(100),
});

export type ManualBoundingBoxParams = zod.infer<typeof ManualBoundingBoxParams>;

export const ManualBoundingBoxMethod = zod.object({
  kind: zod.literal('ManualBoundingBox'),
  settings: ManualBoundingBoxParams,
});

export type ManualBoundingBoxMethod = zod.infer<typeof ManualBoundingBoxMethod>;

// 3D detection method (text-prompt 2D detection -> SAM2 masks -> 3D pose).
// The 3D-pose-estimation fields (and their rationale comments) live in the
// shared `Detection3DPoseEstimationConfig` in visionConfigSchemas.ts (also
// consumed by `@sb/ai`'s skill projection). Here we add the routine-step's
// 2D-detection fields and re-assert the routine-step defaults (OBB_ICP/PRIMITIVE).
export const Detection3DParams = Detection3DPoseEstimationConfig.extend({
  gpuTaskRunnerId: zod.string().default(''),
  // 2D detection settings (text prompt → SAM2 masks → 3D pose)
  textPrompt: zod.string().default(''),
  boxThreshold: zod.number().default(0.35),
  textThreshold: zod.number().default(0.25),
  // Re-assert routine-step defaults (differ from shared schema's no-CAD defaults)
  poseMethod: zod.enum(['OBB_DEPTH', 'OBB_ICP', 'FPFH_ICP']).default('OBB_ICP'),
  cadSource: zod.enum(['NONE', 'PRIMITIVE', 'CAD_FILE']).default('PRIMITIVE'),
});

export type Detection3DParams = zod.infer<typeof Detection3DParams>;

export const Detection3DMethod = zod.object({
  kind: zod.literal('Detection3D'),
  settings: Detection3DParams,
});

export type Detection3DMethod = zod.infer<typeof Detection3DMethod>;

// Locate method union

export const LocateMethod = zod.discriminatedUnion('kind', [
  BlobDetection2DMethod,
  ShapeDetection2DMethod,
  ChessboardDetection2DMethod,
  TextPromptDetection2DMethod,
  CustomCheckpointDetection2DMethod,
  ManualBoundingBoxMethod,
  Detection3DMethod,
  DinoFastMethod,
]);

export type LocateMethod = zod.infer<typeof LocateMethod>;

// GPUTaskRunner vision inference response types

export const CheckpointBasedInferenceErrorCode = zod.nativeEnum(ErrorCode);

export type CheckpointBasedInferenceErrorCode = zod.infer<
  typeof CheckpointBasedInferenceErrorCode
>;

export const CheckpointBasedInferenceError = zod.object({
  code: CheckpointBasedInferenceErrorCode,
  message: zod.string(),
});

export type CheckpointBasedInferenceError = zod.infer<
  typeof CheckpointBasedInferenceError
>;

export const CheckpointBasedInferenceResult = zod.object({
  // Detector box + optional SAM2 segment. Matches the proto wire shape
  // (CheckpointBasedInferenceResult.blobs is LocateMatch[]) so the GPU
  // producers' safeParse accepts it: box → detection, contour/mask → segment.
  blobs: zod.array(LocateMatch).default([]),
  processingTimeMs: zod.number().optional(),
  modelPath: zod.string().optional(),
});

export type CheckpointBasedInferenceResult = zod.infer<
  typeof CheckpointBasedInferenceResult
>;

export const RunCheckpointBasedInferenceResponse = zod
  .object({
    data: CheckpointBasedInferenceResult,
    error: CheckpointBasedInferenceError,
  })
  .partial();
export type RunCheckpointBasedInferenceResponse = zod.infer<
  typeof RunCheckpointBasedInferenceResponse
>;

// Result of a Roboflow object-detection run. vision-bot maps the detector's
// predictions into LocateMatch (box only, segment unset), so this feeds the
// same plane-projection pipeline as checkpoint inference.
export const RoboflowDetectionResult = zod.object({
  blobs: zod.array(LocateMatch),
  processingTimeMs: zod.number().optional(),
});
export type RoboflowDetectionResult = zod.infer<typeof RoboflowDetectionResult>;

export const RunRoboflowDetectionResponse = zod
  .object({
    data: RoboflowDetectionResult,
    error: CheckpointBasedInferenceError,
  })
  .partial();
export type RunRoboflowDetectionResponse = zod.infer<
  typeof RunRoboflowDetectionResponse
>;

/**
 * A workflow run's result, which now carries more than a model run's.
 *
 * This reused `RoboflowDetectionResult` while the two payloads were identical,
 * on the note that the separate envelope existed in case a workflow result ever
 * needed to carry more. It does: only a WORKFLOW can declare an
 * `active_learning_status` output, so putting the field on the shared result
 * would give the model-run RPC a field nothing ever sets.
 */
export const RoboflowWorkflowDetectionResult = RoboflowDetectionResult.extend({
  /**
   * What the workflow's `active_learning_status` output said on this run.
   *
   * Absent when the workflow declares no such output, or from a vision-bot
   * predating the field. REPORTING ONLY: it carries what the upload block
   * answered, by default only that the upload was queued, and never decides
   * whether a capture is written. A routine's own records go to local disk, S3
   * or FTP regardless.
   */
  activeLearningStatus: zod.string().optional(),
});
export type RoboflowWorkflowDetectionResult = zod.infer<
  typeof RoboflowWorkflowDetectionResult
>;

export const RunRoboflowWorkflowDetectionResponse = zod
  .object({
    data: RoboflowWorkflowDetectionResult,
    error: CheckpointBasedInferenceError,
  })
  .partial();
export type RunRoboflowWorkflowDetectionResponse = zod.infer<
  typeof RunRoboflowWorkflowDetectionResponse
>;
